import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { useApiClient } from "@/infrastructure/rest";
import { DossierDetailsEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { DossierDetailsDto, NazorgDto, PatchDossierDetailsDto, PrognoseDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DOSSIER_DETAILS_QUERY_KEY, NAZORG_QUERY_KEY } from "./constants/query-key.ts";
import { DossierDetails } from "./domain/details";
import { Nazorg } from "./domain/nazorg";

export function useGetDossierDetailsById(id: string) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, id, DOSSIER_DETAILS_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<DossierDetailsDto>(`/dossiers/${id}/details`);
            return data ? DossierDetails.fromDto(parseInt(id), data) : null;
        },
    });
}

export type PatchDossierDetails = PatchDossierDetailsDto & {
    dossierId: string;
};

export function usePatchDossierDetailsById() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...payload }: PatchDossierDetails) => {
            await apiClient.patch(`/dossiers/${dossierId}/details`, payload);
        },
        onSuccess: async (_data, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
    });
}

type PutDossierPrognose = PrognoseDto & {
    dossierId: string;
};

export function usePutPrognoseById() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...payload }: PutDossierPrognose) => {
            await apiClient.put(`/dossiers/${dossierId}/prognose`, payload);
        },
        onSuccess: async (_data, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
    });
}

export function useGetNazorg(dossierId: string, enabled?: boolean) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, NAZORG_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<NazorgDto>(`/dossiers/${dossierId}/nazorg`);
            return data ? Nazorg.fromDto(data) : null;
        },
        enabled,
    });
}

type PutNazorg = {
    dossierId: string;
    betrokkenTreinen: string[];
};

export function usePutNazorg() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, betrokkenTreinen }: PutNazorg) => {
            await apiClient.put(`/dossiers/${dossierId}/nazorg`, betrokkenTreinen);
        },
        onSuccess: async (_data, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
    });
}
//todo in other story: add ws event for nazorg (separate channel or refactor to include nazorg in dossier details and use dossier details event)

export function useRealtimeDossierDetails() {
    const { handleEvent } = useUpdateDossierPropertyQueriesData("details");

    useRealtimeTopic<DossierDetailsEvent>("dossierDetails", {
        onMessage: (message) => {
            const { details, dossierId, reason } = message;
            handleEvent(dossierId, dossierId, reason, DossierDetails.fromDto(dossierId, details));
        },
    });
}
